import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { OnlineOpportunitiesChapter1 } from '@/components/stories/online-opportunities/chapter-1';
import { OnlineOpportunitiesChapter2 } from '@/components/stories/online-opportunities/chapter-2';
import { SurveyBackground } from '@/components/survey-background';
import data from '@/data/online-opportunities.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as OnlineOpportunitiesData }),
  getStoryProps('online-opportunities', 'online-risks')
);

export default function OnlineOpportunities(
  props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('online-opportunities');
  const pageTitle = t({ id: 'online-opportunities.title', message: 'Online opportunities' });
  const pageDescription = t({
    id: 'online-opportunities.description',
    message: "It's a digital world. How connected is your generation?",
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.hutspot} hasBackButton>
        <StoryHeader
          backgroundId={storySlug}
          css={{
            backgroundPosition: 'center bottom',
            '@bp9': {
              backgroundSize: '100% auto',
            },
          }}
        >
          <StoryTheme>
            <Trans id="online-opportunities.theme">Digital Tech</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="online-opportunities.tagline">
              <Text type="heading">
                It's a digital world. How <span>connected</span> is your generation?
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <SurveyBackground />

        <OnlineOpportunitiesChapter1 data={data} />
        <OnlineOpportunitiesChapter2 data={data} />

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@media screen and (min-aspect-ratio: 1250/900)': {
              marginBlockStart: 'calc(50vh + 25vw)',
            },
          }}
          relatedStoryId={relatedStoryId}
          sharing={{
            title: t({ id: 'online-opportunities.sharing.title', message: 'Online opportunities' }),
            text: t({
              id: 'online-opportunities.sharing.text',
              message: "It's a digital world. How connected is your generation?",
            }),
            url: sharingUrl,
          }}
        >
          <Trans id="online-opportunities.closing-question">
            If every child in the world were online, how far would this really go in creating{' '}
            <AnnotatedType>equal opportunities for all?</AnnotatedType>
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
